import { useState, useEffect } from 'react'
import { pdf } from '@react-pdf/renderer'
import { Slider } from '@klear/ui/components/slider'
import { Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react'
import { Modal } from '@klear/ui/components/modal'
import { useFileDownload } from '@klear/ui/lib/download-file'
import isNumber from 'lodash/isNumber'

interface PdfModalProps {
  title: string
  pdfDocument?: React.ReactElement
  pdfUrl?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  downloadable?: boolean
  downloadUrl?: string
}

const PdfViewerModal = ({
  title,
  pdfDocument,
  pdfUrl,
  open,
  onOpenChange,
  downloadable,
  downloadUrl
}: PdfModalProps) => {
  const [fileUrl, setFileUrl] = useState<string>('')
  const [scale, setScale] = useState<number>(1.0)
  const [error, setError] = useState<Error | null>(null)
  const { startDownload, isDownloading } = useFileDownload()

  const downloadTarget = downloadUrl ?? fileUrl
  const downloadName = downloadTarget.startsWith('blob:') ? `${title.replace(/\.pdf$/i, '')}.pdf` : undefined

  useEffect(() => {
    if (!open) return

    if (pdfUrl) {
      setFileUrl(pdfUrl)
      setError(null)
      return
    }

    if (pdfDocument) {
      const generatePdf = async () => {
        try {
          const docInstance = pdf()
          docInstance.updateContainer(pdfDocument)
          const blob = await docInstance.toBlob()
          const url = URL.createObjectURL(blob)
          setFileUrl(url)
          setError(null)
        } catch (err) {
          console.error('Error generating PDF:', err)
          setError(err as Error)
        }
      }

      generatePdf()

      return () => {
        setFileUrl((prev) => {
          if (prev && prev.startsWith('blob:')) {
            URL.revokeObjectURL(prev)
            return ''
          }
          return ''
        })
      }
    }
  }, [pdfDocument, pdfUrl, open])

  return (
    <Modal
      title={title}
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <div className="flex flex-col gap-2 justify-center w-full">
          <div className="flex items-center justify-ce gap-3 w-64 mx-auto">
            <ZoomOut className="w-6 h-6 text-muted-foreground" />
            <Slider
              min={0.5}
              max={3}
              step={0.05}
              value={[scale]}
              onValueChange={([val]) => setScale(isNumber(val) ? val : scale)}
              aria-label="Zoom"
            />
            <ZoomIn className="w-6 h-6 text-muted-foreground" />
            <span className="text-sm w-12 text-center">{Math.round(scale * 100)}%</span>
          </div>
        </div>
      }
    >
      <>
        {!error && fileUrl && (
          <div>
            {downloadable && (
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-1 bg-muted rounded hover:bg-muted/80 disabled:opacity-60"
                  onClick={() => startDownload(downloadTarget, downloadName)}
                  disabled={isDownloading}
                >
                  {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  <span>Download</span>
                </button>
              </div>
            )}
            <div className="border rounded-lg bg-card shadow-sm w-full max-h-[60vh] h-[95%] overflow-auto mt-5">
              <div className="text-center" style={{ transform: `scale(${scale})`, transformOrigin: 'top center' }}>
                <iframe
                  src={fileUrl}
                  className="w-full h-[800px] border-0"
                  title={title}
                  onError={(e) => {
                    console.error('PDF loading error:', e)
                    console.error('Attempted URL:', fileUrl)
                    setError(new Error('Failed to load PDF'))
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {error && <div className="text-red-500">{error.message}</div>}
      </>
    </Modal>
  )
}

export default PdfViewerModal
